/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-empty-function */
import { HttpClientModule, provideHttpClient, withFetch } from '@angular/common/http';
import {
    APP_INITIALIZER,
    EnvironmentProviders,
    LOCALE_ID,
    NgModule,
    provideExperimentalZonelessChangeDetection,
    Provider
} from '@angular/core';
import { BrowserModule, provideClientHydration, withNoHttpTransferCache } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { DfAccountModule } from '@df/account/df-account.module';
import { DfApiModule } from '@df/api/df-api.module';
import { DfBrowserModule } from '@df/browser/df-browser.module';
import { DfCatalogUiModule } from '@df/catalog-ui/df-catalog-ui.module';
import { FilterAttributeOption } from '@df/catalog-ui/listing/filter/filter-attribute-option';
import { FilterAttributeRange } from '@df/catalog-ui/listing/filter/filter-attribute-range';
import { IsBannerPluginService } from '@df/catalog-ui/listing/plugin/banner/is-banner-plugin.service';
import { ProductListingFactory } from '@df/catalog-ui/listing/product-listing.factory';
import { RecentlyViewedEngine } from '@df/catalog-ui/recently-viewed/recently-viewed-engine';
import { CategoryEngine } from '@df/catalog-ui/related-products/category-engine';
import { CrosssellEngine } from '@df/catalog-ui/related-products/crosssell-engine';
import { ManualEngine } from '@df/catalog-ui/related-products/manual-engine';
import { RelatedEngine } from '@df/catalog-ui/related-products/related-engine';
import '@df/catalog-ui/related-products/upsell-engine';
import { AttributeResource } from '@df/catalog/attribute/attribute.resource';
import { CategoryResource } from '@df/catalog/category/category.resource';
import { CustomformResource } from '@df/catalog/customform/customform.resource';
import { DfCatalogModule } from '@df/catalog/df-catalog.module';
import { ProductResource } from '@df/catalog/product/product.resource';
import { ReviewResource } from '@df/catalog/review/review.resource';
import { RouteResource } from '@df/catalog/route/route.resource';
import { StockResource } from '@df/catalog/stock/stock.resource';
import { Store } from '@df/catalog/store/store';
import { StoreResource } from '@df/catalog/store/store.resource';
import { App } from '@df/core/app';
import { DfCoreModule } from '@df/core/df-core.module';
import { MetaAlternateService } from '@df/core/meta/meta-alternate.service';
import { AbModule } from '@df/module-ab/ab.module';
import { AbCms2Module } from '@df/module-ab/cms2/ab-cms2.module';
import { DfModuleGiftcardsModule } from '@df/module-giftcards/df-module-giftcards.module';
import { DfModuleGoogleMapsModule } from '@df/module-google-maps/df-module-google-maps.module';
import { DfModuleGtmModule } from '@df/module-gtm/df-module-gtm.module';
import { GtmCheckoutPlugin } from '@df/module-gtm/gtm.checkout.plugin';
import { GtmService } from '@df/module-gtm/gtm.service';
import { DfModuleImageModule } from '@df/module-image/df-module-image.module';
import { KlevuSearchModule } from '@df/module-klevu-search/klevu-search.module';
import { PcaAddressSearcherModule } from '@df/module-pca/pca.module';
import { DfModulePrivacyModule } from '@df/module-privacy/df-module-privacy.module';
import { DfModuleProductConfigurationModule } from '@df/module-product-configuration/df-module-product-configuration.module';
import { SentryModule } from '@df/module-sentry/sentry.module';
import { DfThumborModule } from '@df/module-thumbor/df-thumbor.module';
import { ProcessorService } from '@df/ng/processor.service';
import { surviveTreeShaking } from '@df/ng/survive-tree-shaking';
import { DfSearchModule } from '@df/search/df-search.module';
import { Wishlist } from '@df/session/api/wishlist/wishlist';
import { DfSessionModule } from '@df/session/df-session.module';
import { DfUiModule } from '@df/ui/df-ui.module';
import { DEFAULT_ADDRESS_SEARCHER_OPTIONS } from '@df/ui/form/address-searcher';
import { DfSsrModule } from '@df/universal/client/df-ssr.module';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsPageResource } from '@icms/core/resource/page/icms-page.resource';
import { AppRootComponent } from './app-root.component';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BasketModule } from './basket/basket.module';
import './catalog/attribute/attribute.decorator';
import './catalog/category/category.decorator';
import { Error500Component } from './catalog/error/error-500.component';
import { ProductListingFactoryDecorator } from './catalog/listing/product-listing.factory.decorator';
import { LocalPageResource } from './catalog/page/local/local-page.resource';
import './catalog/page/registerAdditionalPageFields';
import './catalog/product/product.decorator';
import './catalog/store/store.decorator';
import { CmsModule } from './cms/cms.module';
import { ICMS_CONFIG } from './icms.config';
import { MaterialsCalculatorModule } from './materials-calculator/materials-calculator.module';
import { PageModule } from './page/page.module';
import { SharedModule } from './shared.module';

const providers: (Provider | EnvironmentProviders)[] = [];
if (typeof window !== 'undefined') {
    providers.push(provideExperimentalZonelessChangeDetection());
}

@NgModule({
    declarations: [AppRootComponent, AppComponent, Error500Component],
    imports: [
        AbCms2Module.forRoot(),
        AbModule.forRoot({}),
        ICmsClientModule.forRoot(ICMS_CONFIG),
        DfCoreModule.forRoot(),
        DfModuleGtmModule.forRoot(),
        DfAccountModule.forRoot(),
        DfApiModule,
        DfBrowserModule,
        DfCatalogModule,
        DfCatalogUiModule.forRoot({
            listing: {
                resetTop: 0,
                category: {
                    infScrollInit: [36, 36, 36, 36],
                    infScrollStep: [36, 36, 36, 36],
                    size: [
                        {
                            size: 1,
                            onSize: ['S']
                        },
                        {
                            size: 2,
                            onSize: ['S', 'M']
                        },
                        {
                            size: 3,
                            onSize: ['L', 'X']
                        },
                        {
                            size: 4,
                            onSize: ['L', 'X']
                        }
                    ],
                    defaultSize: [1, 2, 4, 4],
                    sort: [
                        {
                            label: 'Recommended',
                            default: true,
                            engine: 'klevu'
                        },
                        {
                            label: 'New',
                            code: 'created_at',
                            desc: true
                        },
                        {
                            label: 'Highest price',
                            code: 'price',
                            desc: true
                        },
                        {
                            label: 'Lowest price',
                            code: 'price'
                        }
                    ]
                }
            },

            pdp: {
                configurable: {
                    respectStockSettings: true
                }
            }
        }),

        DfUiModule.forRoot({
            price: {
                thousandSeparator: ',',
                initDisplayTaxWith: false,
                precision: 2
            }
        }),

        DfModuleGoogleMapsModule.forRoot({
            mapOptions: {
                zoom: 5,
                scrollwheel: false
            },
            storeToMarker: (store: Store, options: google.maps.MarkerOptions) => {
                return {
                    ...options,
                    info: `
                    <div class="center font-default p-a-1">
                    <h5 class="h4 p-b-1">${store.data?.store_name || 'store name not set'}</h5>
                    <p class="p-b-1 font-2 fs-4">${store.data?.address || 'address not set'}</p>
                    <p class="font-2"><a class="col-2" href="tel:${store.data?.phone || 'phone not set'}">${
                        store.data?.phone || 'phone not set'
                    }</a></p>
                    </div>`
                };
            }
        }),
        DfThumborModule.forRoot(),
        DfSearchModule.forRoot(),
        SentryModule.forRoot({
            dsn: 'https://4610d2d8a0c13c966f06543da9c69cbf@sentry.c1a.tomandco.uk/48'
        }),
        DfSessionModule.forRoot(),
        DfModuleGiftcardsModule,
        DfModuleProductConfigurationModule.forRoot(),
        DfModulePrivacyModule.forRoot(),
        DfModuleImageModule.forRoot(),
        BrowserModule,
        BrowserAnimationsModule,
        BasketModule,
        MaterialsCalculatorModule,
        CmsModule,
        HttpClientModule,
        PageModule,
        SharedModule,
        PcaAddressSearcherModule.forRoot(),
        KlevuSearchModule.forRoot(),
        // routes on end
        AppRoutingModule,
        DfSsrModule
    ],
    bootstrap: [AppComponent],
    providers: [
        provideHttpClient(withFetch()),
        provideClientHydration(withNoHttpTransferCache()),
        ...providers,
        { provide: ICmsPageResource, useExisting: LocalPageResource },
        { provide: ProductListingFactory, useClass: ProductListingFactoryDecorator },
        {
            provide: APP_INITIALIZER,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            useFactory: (gtmService: GtmService) => () => {},
            deps: [GtmService],
            multi: true
        },
        {
            provide: LOCALE_ID,
            useFactory: (app: App) => {
                return app.dfConfig.ms?.sites[app.dfConfig.ms.siteId]?.lang?.[0] || 'en';
            },
            deps: [App]
        }
    ]
})
export class AppModule {
    constructor(
        processorService: ProcessorService,
        app: App,
        isBannerPluginService: IsBannerPluginService,
        metaAlternate: MetaAlternateService,
        attributeResource: AttributeResource,
        vategoryResource: CategoryResource,
        // groupResource: GroupResource,
        productResource: ProductResource,
        reviewResource: ReviewResource,
        routeResource: RouteResource,
        stockResource: StockResource,
        storeResource: StoreResource,
        wishlist: Wishlist,
        gtmCheckoutPlugin: GtmCheckoutPlugin,
        customformResource: CustomformResource
    ) {
        DEFAULT_ADDRESS_SEARCHER_OPTIONS.runOnQueryChange = false;
        // Create global Service Injector.
        ProcessorService.subject.complete();
        surviveTreeShaking(
            CategoryEngine,
            CrosssellEngine,
            FilterAttributeOption,
            FilterAttributeRange,
            ManualEngine,
            RelatedEngine,
            RecentlyViewedEngine
        );
    }
}
